import { useDispatch, useSelector } from "react-redux";
import { Box, Text, useThemeUI } from "theme-ui";
import nl2br from "react-nl2br";

import { selectCustomisationNotifications, selectUserChoices } from "store/app";
import { displayModal } from "store/app/appSlice";
import { FieldsSelector } from "components/Forms";
import { HelpButton, LearnMore, Notifications } from "components";
import { useTranslation } from "hooks";
import { FORM_COMPONENTS } from "utils/constants";

export default function Fields({
  customisations,
  userChoiceValues,
  selectedCustomisationId,
  focusedField: focusedFields = [],
  setFocusedField,
  goBackFocusedFields,
}) {
  const {
    theme: { isMobile },
  } = useThemeUI();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const userChoices = useSelector(selectUserChoices);

  const selectedCustomisation =
    customisations.find(({ id }) => selectedCustomisationId === id) || {};

  const customisationNotifications = useSelector(
    selectCustomisationNotifications
  );

  const [focusedField, ..._restFocusedFields] = focusedFields;

  const filteredFields = (selectedCustomisation?.fields || []).filter(
    ({ name }) => !focusedField || name === focusedField
  );

  const hasOptionalFields =
    filteredFields.length > 1 &&
    filteredFields.some(({ component, isRequired }) => {
      return (
        component === FORM_COMPONENTS.GOALIE_GEAR_COLOR_OPTIONS &&
        isRequired === false
      );
    });

  return (
    <>
      {filteredFields.map((field) => (
        <FieldsSelector
          key={field.name}
          customisationTitle={selectedCustomisation.title}
          component={field.component}
          userChoices={userChoices}
          userChoiceValues={userChoiceValues}
          name={field.name}
          placeholder={field.placeholder}
          as={field.as}
          type={field.type}
          initialValue={field.initialValue}
          maxLength={field.maxLength}
          isRequired={field.isRequired}
          options={field.options}
          fields={field.fields}
          label={field.label}
          labelIcon={field.labelIcon}
          checkedValue={field.checkedValue}
          uncheckedValue={field.uncheckedValue}
          isDisabledIf={field.isDisabledIf}
          width={field.width}
          focusedField={focusedFields}
          setFocusedField={setFocusedField}
          goBackFocusedFields={goBackFocusedFields}
          hint={field.hint}
          info={field.info}
          hasOptionalFields={hasOptionalFields}
          defaultValueToSetOnRender={field.defaultValueToSetOnRender}
          toolTip={field.toolTip}
          props={field.props}
        />
      ))}

      {selectedCustomisation?.help && (
        <Box>
          <HelpButton
            onClick={() =>
              dispatch(
                displayModal({
                  modal: "Modal",
                  modalProps: selectedCustomisation.help,
                })
              )
            }
          >
            {selectedCustomisation.help.buttonLabel}
          </HelpButton>
        </Box>
      )}

      {selectedCustomisation?.learnMore && (
        <LearnMore
          href={selectedCustomisation.learnMore.link}
          target="_blank"
          rel="noopener noreferrer"
        >
          {selectedCustomisation.learnMore.buttonLabel}
        </LearnMore>
      )}

      {selectedCustomisation.hint && (
        <Text
          variant="copy2"
          key="hint"
          sx={{
            color: "grey5",
            fontFamily: "roboto",
            lineHeight: 1,
            textAlign: ({ isMobile }) => (isMobile ? "center" : "initial"),
          }}
        >
          {nl2br(selectedCustomisation.hint)}
        </Text>
      )}

      {hasOptionalFields && (
        <Text
          variant="h5Light"
          key="note"
          sx={{
            fontFamily: "dinCondensed",
            textAlign: ({ isMobile }) => (isMobile ? "center" : "initial"),
          }}
        >
          *{t("OPTIONAL")}
        </Text>
      )}

      {isMobile && customisationNotifications.length > 0 && (
        <Notifications notifications={customisationNotifications} />
      )}
    </>
  );
}
